import React from "react";
import { Link } from "react-router-dom";
import { FaArrowLeft, FaArrowRight, FaEnvelope } from "react-icons/fa";
import LanguageSwitch from "../components/LanguageSwitch";
import { useLanguage } from "../contexts/LanguageContext";
import logo from "../assets/ourdo-tail-curve-wordmark.svg";
import "../styles.css";
import "../learning.css";

type LocalizedText = { id: string; en: string };

const audiences = [
  {
    title: { id: "Individuals", en: "Individuals" },
    body: {
      id: "Untuk career switcher, junior engineer, student, atau learner yang butuh arahan praktis dan pendampingan yang lebih personal.",
      en: "For career switchers, junior engineers, students, or learners who need practical direction and more personal guidance.",
    },
  },
  {
    title: { id: "Teams", en: "Teams" },
    body: {
      id: "Untuk tim engineering, project, atau program yang ingin meningkatkan capability lewat sesi yang dekat dengan pekerjaan nyata.",
      en: "For engineering, project, or programme teams that want to build capability through learning tied to real work.",
    },
  },
  {
    title: { id: "Communities", en: "Communities" },
    body: {
      id: "Untuk komunitas atau organisasi yang membutuhkan workshop, cohort learning, facilitation, dan programme upskilling.",
      en: "For communities or organisations that need workshops, cohort learning, facilitation, and programme upskilling.",
    },
  },
];

const programmes = [
  {
    type: "PRIVATE TECHNICAL LEARNING",
    title: { id: "Private Backend", en: "Private Backend" },
    body: {
      id: "Belajar membangun backend dan API secara terstruktur lewat sesi privat, practice, dan problem solving yang relevan dengan pekerjaan engineering.",
      en: "Learn backend and API development through structured private sessions, practice, and problem solving connected to real engineering work.",
    },
    meta: "1-on-1 · Online",
  },
  {
    type: "PRIVATE TECHNICAL LEARNING",
    title: { id: "Private Frontend", en: "Private Frontend" },
    body: {
      id: "Belajar membangun interface React, responsive UI, dan integrasi API sampai punya alur kerja frontend yang lebih utuh.",
      en: "Learn to build React interfaces, responsive UI, and API integrations while developing a more complete frontend workflow.",
    },
    meta: "1-on-1 · Online",
  },
  {
    type: "FOUNDATIONAL LEARNING",
    title: { id: "Coding for Kids", en: "Coding for Kids" },
    body: {
      id: "Kelas untuk SD dan SMP yang berfokus pada logika, problem solving, dan mini-project agar coding terasa konkret dan menyenangkan.",
      en: "Classes for elementary and middle-school learners focused on logic, problem solving, and mini-projects that make coding concrete and engaging.",
    },
    meta: "Private · SD / SMP",
  },
  {
    type: "1-ON-1 GUIDANCE",
    title: { id: "Ourdo Buddy Session", en: "Ourdo Buddy Session" },
    body: {
      id: "Sesi 1-on-1 untuk membahas problem kerja, code, interview preparation, career direction, atau transisi role dengan next step yang jelas.",
      en: "A 1-on-1 session for work problems, code discussions, interview preparation, career direction, or role transitions with clear next steps.",
    },
    meta: "60 minutes · Online",
  },
  {
    type: "LEARNING OPERATIONS PLATFORM",
    title: { id: "Ourdo Atlas", en: "Ourdo Atlas" },
    body: {
      id: "LMS / learning operations MVP untuk organisasi yang butuh satu sistem untuk mengelola akses admin, mentor, dan student serta membangun workflow pembelajaran yang lebih terstruktur.",
      en: "An LMS / learning operations MVP for organisations that need one system for admin, mentor, and student access and a more structured learning workflow.",
    },
    meta: "Custom LMS foundation · MVP",
  },
  {
    type: "TEAM & COMMUNITY",
    title: { id: "Learning Experience Design", en: "Learning Experience Design" },
    body: {
      id: "Membantu organisasi merancang learning journey yang lebih terarah, interaktif, dan relevan dengan outcome yang ingin dicapai.",
      en: "Helping organisations design learning journeys that are more focused, interactive, and connected to the outcomes they want.",
    },
    meta: "Custom programme",
  },
  {
    type: "TEAM & COMMUNITY",
    title: { id: "Digital Facilitation & Community Upskilling", en: "Digital Facilitation & Community Upskilling" },
    body: {
      id: "Workshop atau programme untuk meningkatkan kemampuan fasilitasi, digital workflow, dan capability komunitas atau tim.",
      en: "Workshops or programmes that strengthen facilitation, digital workflows, and team or community capability.",
    },
    meta: "Custom programme",
  },
];

const steps = [
  {
    title: { id: "Tell us the goal", en: "Tell us the goal" },
    body: {
      id: "Mulai dari skill gap, problem kerja, kebutuhan tim, atau outcome programme yang ingin dicapai.",
      en: "Start with the skill gap, work problem, team need, or programme outcome you want to achieve.",
    },
  },
  {
    title: { id: "Shape the programme", en: "Shape the programme" },
    body: {
      id: "Pilih offering yang sudah ada atau susun programme custom berdasarkan audience, waktu, dan konteks.",
      en: "Choose an existing offer or shape a custom programme around the audience, timing, and context.",
    },
  },
  {
    title: { id: "Learn through practice", en: "Learn through practice" },
    body: {
      id: "Delivery menggabungkan penjelasan, practice, discussion, mentoring, dan contoh yang dekat dengan pekerjaan nyata.",
      en: "Delivery combines explanation, practice, discussion, mentoring, and examples tied to real work.",
    },
  },
  {
    title: { id: "Review & next steps", en: "Review & next steps" },
    body: {
      id: "Tutup dengan review, feedback, dan langkah berikutnya agar learning tidak berhenti di sesi.",
      en: "Close with review, feedback, and clear next steps so the learning does not stop when the session ends.",
    },
  },
];

const OurLearningPage: React.FC = () => {
  const { language } = useLanguage();
  const isEnglish = language === "en";
  const t = (text: LocalizedText) => (isEnglish ? text.en : text.id);

  const contactEmail = "mailto:official@ourdo.id?subject=Learning%20or%20training%20inquiry%20for%20Ourdo%20Indonesia";

  return (
    <div className="ourdo-site learning-page">
      <header className="ourdo-header">
        <div className="ourdo-container ourdo-nav">
          <Link to="/" className="ourdo-brand">
            <img src={logo} alt="OURDO Indonesia — Tail Curve" />
            <div>
              <strong>INDONESIA</strong>
              <span>Learning & Capacity Building</span>
            </div>
          </Link>

          <nav className="ourdo-nav-links" aria-label="Learning navigation">
            <a href="#audience">{isEnglish ? "Who it's for" : "Untuk siapa"}</a>
            <a href="#programmes">{isEnglish ? "Programmes" : "Programme"}</a>
            <a href="#process">{isEnglish ? "How it works" : "Cara kerja"}</a>
          </nav>

          <div className="ourdo-nav-actions">
            <LanguageSwitch />
            <Link className="ourdo-btn ourdo-btn-secondary" to="/">
              <FaArrowLeft /> {isEnglish ? "Back home" : "Kembali"}
            </Link>
          </div>
        </div>
      </header>

      <main>
        <section className="ourdo-container learning-hero">
          <div>
            <p className="ourdo-eyebrow">LEARNING & CAPACITY BUILDING</p>
            <h1>
              {isEnglish
                ? "Build practical skills people can use after the session ends."
                : "Bangun skill praktis yang tetap berguna setelah sesi selesai."}
            </h1>
            <p className="ourdo-lead">
              {isEnglish
                ? "Ourdo Indonesia designs and delivers practical learning for individuals, teams, and communities across software engineering, digital workflows, facilitation, and selected data or spatial topics."
                : "Ourdo Indonesia merancang dan menjalankan learning praktis untuk individu, tim, dan komunitas di area software engineering, digital workflow, facilitation, serta topik data atau spatial tertentu."}
            </p>

            <div className="ourdo-actions">
              <a className="ourdo-btn ourdo-btn-primary" href="#programmes">
                {isEnglish ? "Explore programmes" : "Lihat programme"} <FaArrowRight />
              </a>
              <a className="ourdo-btn ourdo-btn-secondary" href={contactEmail}>
                {isEnglish ? "Discuss team training" : "Diskusikan training tim"}
              </a>
            </div>
          </div>

          <aside className="learning-proof-panel">
            <p className="ourdo-eyebrow">{isEnglish ? "PUBLISHED TRACK RECORD" : "PUBLISHED TRACK RECORD"}</p>
            <div className="learning-proof-grid">
              <article>
                <strong>40+</strong>
                <span>{isEnglish ? "Events delivered" : "Acara terlaksana"}</span>
              </article>
              <article>
                <strong>145+</strong>
                <span>{isEnglish ? "Learners supported" : "Peserta terbimbing"}</span>
              </article>
              <article>
                <strong>96%</strong>
                <span>{isEnglish ? "Published satisfaction rate" : "Tingkat kepuasan terpublikasi"}</span>
              </article>
            </div>
            <p className="learning-proof-note">
              {isEnglish
                ? "These figures were published on the previous Ourdo site and are shown here as published track record while source documentation is being consolidated."
                : "Angka ini sudah dipublikasikan di website Ourdo sebelumnya dan ditampilkan sebagai published track record sambil source dokumentasinya dirapikan."}
            </p>
          </aside>
        </section>

        <section className="ourdo-section" id="audience">
          <div className="ourdo-container">
            <div className="ourdo-section-heading">
              <p className="ourdo-eyebrow">{isEnglish ? "WHO IT'S FOR" : "UNTUK SIAPA"}</p>
              <h2>{isEnglish ? "Different audiences need different learning formats." : "Audience yang berbeda butuh format learning yang berbeda."}</h2>
            </div>

            <div className="ourdo-grid-3">
              {audiences.map((item) => (
                <article className="learning-audience-card" key={item.title.en}>
                  <h3>{t(item.title)}</h3>
                  <p>{t(item.body)}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="ourdo-section" id="programmes">
          <div className="ourdo-container">
            <div className="ourdo-section-heading">
              <p className="ourdo-eyebrow">{isEnglish ? "WHAT YOU CAN JOIN OR BUY" : "YANG BISA DIIKUTI ATAU DIBELI"}</p>
              <h2>{isEnglish ? "Clear offers for individuals, teams, and communities." : "Offering yang jelas untuk individu, tim, dan komunitas."}</h2>
              <p>
                {isEnglish
                  ? "Some programmes are ready-made. Team and community engagements can be adapted around a specific capability goal."
                  : "Sebagian programme sudah siap diambil. Untuk tim dan komunitas, formatnya bisa disesuaikan dengan capability goal tertentu."}
              </p>
            </div>

            <div className="learning-programme-grid">
              {programmes.map((programme) => (
                <article className="learning-programme-card" key={programme.title.en}>
                  <span>{programme.type}</span>
                  <h3>{t(programme.title)}</h3>
                  <p>{t(programme.body)}</p>
                  <strong>{programme.meta}</strong>
                </article>
              ))}
            </div>
          </div>
        </section>


        <section className="ourdo-section">
          <div className="ourdo-container">
            <div className="ourdo-baruna-block">
              <div>
                <p className="ourdo-eyebrow">LEARNING PRODUCT · OURDO ATLAS</p>
                <h2>{isEnglish ? "Need more than training sessions? Build the learning operations around them." : "Butuh lebih dari training session? Bangun learning operations di sekitarnya."}</h2>
                <p>{isEnglish ? "Ourdo Atlas is an LMS / learning operations MVP for organisations that need role-based access for admins, mentors, and students. It can be used as a foundation for custom learning workflows instead of forcing every programme into a generic course platform." : "Ourdo Atlas adalah LMS / learning operations MVP untuk organisasi yang membutuhkan role-based access bagi admin, mentor, dan student. Platform ini bisa menjadi fondasi custom learning workflow daripada memaksa setiap programme masuk ke course platform yang generik."}</p>
                <div className="ourdo-actions">
                  <a className="ourdo-btn ourdo-btn-light" href="https://atlas.ourdo.id/login" target="_blank" rel="noreferrer">
                    {isEnglish ? "Open Atlas" : "Buka Atlas"}
                  </a>
                  <a className="ourdo-btn ourdo-btn-outline-light" href={contactEmail}>
                    {isEnglish ? "Discuss a custom LMS" : "Diskusikan custom LMS"}
                  </a>
                </div>
              </div>
              <div>
                <div className="learning-atlas-media">
                  <img src="/assets/portfolio/ourdo-atlas-cover.webp" alt="Ourdo Atlas learning operations interface" loading="lazy" />
                </div>
                <div className="ourdo-baruna-facts">
                <div><span>01</span><strong>Admin operations</strong></div>
                <div><span>02</span><strong>Mentor workflow</strong></div>
                <div><span>03</span><strong>Student experience</strong></div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="ourdo-section" id="process">
          <div className="ourdo-container">
            <div className="ourdo-section-heading">
              <p className="ourdo-eyebrow">{isEnglish ? "HOW IT WORKS" : "CARA KERJA"}</p>
              <h2>{isEnglish ? "From learning goal to practical next step." : "Dari learning goal sampai next step yang praktis."}</h2>
            </div>

            <div className="learning-process">
              {steps.map((step, index) => (
                <article key={step.title.en}>
                  <span>0{index + 1}</span>
                  <div>
                    <h3>{t(step.title)}</h3>
                    <p>{t(step.body)}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="ourdo-section">
          <div className="ourdo-container">
            <div className="ourdo-cta-block">
              <div>
                <h2>{isEnglish ? "Need a programme for your team or community?" : "Butuh programme untuk tim atau komunitas?"}</h2>
                <p>
                  {isEnglish
                    ? "Tell us the audience, capability gap, and outcome you want. We can start from an existing programme or shape a focused one."
                    : "Ceritakan audience, capability gap, dan outcome yang ingin dicapai. Kita bisa mulai dari programme existing atau menyusun yang lebih fokus."}
                </p>
              </div>
              <a className="ourdo-btn ourdo-btn-light" href={contactEmail}>
                <FaEnvelope /> {isEnglish ? "Email us about learning" : "Email untuk programme learning"}
              </a>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default OurLearningPage;
